import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    API_DELETE_CLIENT,
    API_CLIENT_DETAIL,
    API_GET_CLIENTS,
    API_UPDATE_CLIENT_STATUS,
    API_UPDATE_CLIENT_REPAYMENT_STATUS,
    API_VIEW_CLIENT_DOCUMENT,
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { apiJson } from '../utils/apiClient';
import { confirmAction } from '../utils/confirmAction';
import DocumentLinks from '../Common/DocumentLinks';

const ClientManagement = () => {
    const { canAccess, user } = useAuth();
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [search, setSearch] = useState({
        client_name: '',
        center_name: '',
        center_no: '',
    });
    const [currentPage, setCurrentPage] = useState(1);
    const clientsPerPage = 10;
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [isClientDetailOpen, setIsClientDetailOpen] = useState(false);
    const [isClientDetailLoading, setIsClientDetailLoading] = useState(false);
    const [clientDetailError, setClientDetailError] = useState('');
    const [statusClient, setStatusClient] = useState(null);
    const [repaymentStatus, setRepaymentStatus] = useState({ repayment_status: 'normal', reason: '', effective_date: '' });
    const canUpdateRepaymentStatus = canAccess('clients', 'edit') && ['manager', 'admin', 'super_admin'].includes(user?.role_key);

    const saveRepaymentStatus = async () => {
        if (!statusClient) return;
        const needsReason = ['deceased', 'exempt'].includes(repaymentStatus.repayment_status);
        const payload = { client_id: statusClient.id, ...repaymentStatus, reason: repaymentStatus.reason.trim() };

        if (!payload.effective_date) {
            setMessage('Select the repayment-status effective date before saving.');
            return;
        }
        if (needsReason && !payload.reason) {
            setMessage(`Enter a reason before marking this client as ${payload.repayment_status}.`);
            return;
        }
        if (!confirmAction('update this repayment status')) return;

        try {
            const { data } = await apiJson(API_UPDATE_CLIENT_REPAYMENT_STATUS, { method: 'POST', body: JSON.stringify(payload) });
            setMessage(data?.message || 'Unable to update repayment status.');
            if (data?.status) { setStatusClient(null); await getAllClients(); }
        } catch (error) {
            setMessage('Unable to update repayment status. Please try again.');
        }
    };

    const getAllClients = useCallback(async () => {
        try {
            setIsLoading(true);
            const { data: responseData } = await apiJson(API_GET_CLIENTS, {
                method: 'POST',
                body: JSON.stringify({ is_all_list: true }),
            });
            setClients(responseData.data || []);
            setFilteredClients(responseData.data || []);
        } catch (error) {
            setMessage('Unable to load clients.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void getAllClients();
    }, [getAllClients]);

    useEffect(() => {
        const filtered = clients.filter((client) =>
            client.client_name?.toLowerCase().includes(search.client_name.toLowerCase()) &&
            client.center_name?.toLowerCase().includes(search.center_name.toLowerCase()) &&
            client.center_no?.toLowerCase().includes(search.center_no.toLowerCase())
        );
        setFilteredClients(filtered);
    }, [clients, search]);

    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        const updatedSearch = { ...search, [name]: value };
        setSearch(updatedSearch);
        setCurrentPage(1);
    };

    const toggleClientStatus = async (clientId) => {
        try {
            const client = clients.find((currentClient) => currentClient.id === clientId);
            const newStatus = Number(client.is_active) === 1 ? 0 : 1;
            if (!confirmAction(`${newStatus ? 'activate' : 'deactivate'} this client`)) return;

            const { data: result } = await apiJson(API_UPDATE_CLIENT_STATUS, {
                method: 'POST',
                body: JSON.stringify({ client_id: clientId, is_active: newStatus }),
            });

            if (result.data?.affectedRows) {
                setClients((prev) =>
                    prev.map((currentClient) =>
                        currentClient.id === clientId ? { ...currentClient, is_active: newStatus } : currentClient
                    )
                );
                setMessage('Client status updated successfully.');
            } else {
                setMessage('Failed to update client status.');
            }
        } catch (error) {
            setMessage('An error occurred while updating the client status.');
        }
    };

    const deleteClient = async (clientId) => {
        if (!confirmAction('archive this client')) return;
        try {
            const { data } = await apiJson(API_DELETE_CLIENT, {
                method: 'POST',
                body: JSON.stringify({ client_id: clientId }),
            });
            setMessage(data?.message || 'Client archived successfully.');
            await getAllClients();
        } catch (error) {
            setMessage('Unable to delete client. Loan records may still be linked.');
        }
    };

    const viewClientDetails = async (clientId) => {
        setIsClientDetailOpen(true);
        setIsClientDetailLoading(true);
        setClientDetailError('');
        setSelectedClient(null);

        try {
            const { data } = await apiJson(API_CLIENT_DETAIL, {
                method: 'POST',
                body: JSON.stringify({ client_id: clientId }),
            });

            if (!data?.status || !data?.data) {
                throw new Error(data?.message || 'Unable to load client details.');
            }

            setSelectedClient(data.data);
        } catch (error) {
            setClientDetailError(error.message || 'Unable to load client details.');
        } finally {
            setIsClientDetailLoading(false);
        }
    };

    const indexOfLastClient = currentPage * clientsPerPage;
    const indexOfFirstClient = indexOfLastClient - clientsPerPage;
    const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);
    const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

    return (
        <div className="container my-5">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                <h2 className="mb-2 mb-md-0 float-left">Client List</h2>
                {canAccess('clients', 'create') ? (
                    <button onClick={() => navigate('/client-details')} className="btn btn-primary float-right width200">
                        Add New Client
                    </button>
                ) : null}
            </div>

            {message ? <div className="alert alert-info">{message}</div> : null}

            {isLoading ? (
                <div className="dashboard-empty-state">Loading clients...</div>
            ) : (
                <div className="table-responsive data-table-shell">
                    <table className="table table-bordered table-striped data-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Center No</th>
                                <th>Center Name</th>
                                <th>Client Name</th>
                                <th>Status</th>
                                <th>Repayment Status</th>
                                <th>Action</th>
                            </tr>
                            <tr>
                                <th></th>
                                <th></th>
                                <th><input type="text" name="center_no" value={search.center_no} onChange={handleSearchChange} className="form-control form-control-sm" placeholder="Search Center No" /></th>
                                <th><input type="text" name="center_name" value={search.center_name} onChange={handleSearchChange} className="form-control form-control-sm" placeholder="Search Center Name" /></th>
                                <th><input type="text" name="client_name" value={search.client_name} onChange={handleSearchChange} className="form-control form-control-sm" placeholder="Search Client Name" /></th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentClients.length > 0 ? (
                                currentClients.map((client, index) => (
                                    <tr key={client.id}>
                                        <td>{indexOfFirstClient + index + 1}</td>
                                        <td>{client.center_no}</td>
                                        <td>{client.center_name}</td>
                                        <td>{client.client_name}</td>
                                        <td>{Number(client.is_active) === 1 ? 'Active' : 'Inactive'}</td>
                                        <td>{client.repayment_status || 'normal'}</td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-2">
                                                {canAccess('clients', 'view') ? (
                                                    <button className="btn btn-sm btn-info" onClick={() => viewClientDetails(client.id)}>
                                                        View Details
                                                    </button>
                                                ) : null}
                                                {canAccess('clients', 'edit') ? (
                                                    <>
                                                        <button className="btn btn-sm btn-warning" onClick={() => navigate(`/client-details/${client.id}`)}>
                                                            Edit
                                                        </button>
                                                        <button className={`btn btn-sm ${Number(client.is_active) === 1 ? 'btn-secondary' : 'btn-success'}`} onClick={() => toggleClientStatus(client.id)}>
                                                            {Number(client.is_active) === 1 ? 'Deactivate' : 'Activate'}
                                                        </button>
                                                        {canUpdateRepaymentStatus ? <button className="btn btn-sm btn-outline-primary" onClick={() => { setStatusClient(client); setRepaymentStatus({ repayment_status: client.repayment_status || 'normal', reason: client.repayment_status_reason || '', effective_date: String(client.repayment_status_effective_date || '').slice(0, 10) || new Date().toISOString().slice(0, 10) }); }}>Repayment Status</button> : null}
                                                    </>
                                                ) : null}
                                                {canAccess('clients', 'delete') ? (
                                                    <button className="btn btn-sm btn-danger" onClick={() => deleteClient(client.id)}>
                                                        Delete
                                                    </button>
                                                ) : null}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center">No clients found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <nav>
                <ul className="pagination justify-content-center">
                    {Array.from({ length: totalPages }, (_, i) => (
                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                            <button onClick={() => setCurrentPage(i + 1)} className="page-link">
                                {i + 1}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            {statusClient ? <div className="modal d-block" tabIndex="-1"><div className="modal-dialog"><div className="modal-content"><div className="modal-header"><h5>Repayment Status: {statusClient.client_name}</h5><button className="btn-close" onClick={() => setStatusClient(null)} /></div><div className="modal-body"><label>Status</label><select className="form-control mb-2" value={repaymentStatus.repayment_status} onChange={(e) => setRepaymentStatus({ ...repaymentStatus, repayment_status: e.target.value })}><option value="normal">Normal</option><option value="od">OD</option><option value="deceased">Deceased</option><option value="exempt">Exempt</option></select><label>Effective Date</label><input type="date" className="form-control mb-2" value={repaymentStatus.effective_date} onChange={(e) => setRepaymentStatus({ ...repaymentStatus, effective_date: e.target.value })} required /><label>Reason</label><textarea className="form-control" value={repaymentStatus.reason} onChange={(e) => setRepaymentStatus({ ...repaymentStatus, reason: e.target.value })} required={['deceased', 'exempt'].includes(repaymentStatus.repayment_status)} /></div><div className="modal-footer"><button className="btn btn-secondary" onClick={() => setStatusClient(null)}>Cancel</button><button className="btn btn-primary" onClick={saveRepaymentStatus}>Save</button></div></div></div></div> : null}

            {isClientDetailOpen ? (
                <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Client Details</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={() => setIsClientDetailOpen(false)} />
                            </div>
                            <div className="modal-body">
                                {isClientDetailLoading ? <p>Loading client details...</p> : null}
                                {clientDetailError ? <div className="alert alert-danger">{clientDetailError}</div> : null}
                                {selectedClient ? (
                                    <div className="row g-3">
                                        {[
                                            ['Client No', selectedClient.client_no],
                                            ['Client Name', selectedClient.client_name],
                                            ['Branch', selectedClient.branch_name],
                                            ['Center', `${selectedClient.center_no || ''} ${selectedClient.center_name || ''}`.trim()],
                                            ['Date of Birth', selectedClient.client_dob],
                                            ['Contact No', selectedClient.contact_no],
                                            ['UID', selectedClient.client_uid],
                                            ['PAN', selectedClient.client_pan],
                                            ['Father Name', selectedClient.client_father_name],
                                            ['Mother Name', selectedClient.client_mother_name],
                                            ['Husband Name', selectedClient.client_husband_name],
                                            ['Business', selectedClient.client_business],
                                            ['Purpose of Loan', selectedClient.purpose_of_loan],
                                            ['Address', selectedClient.client_address],
                                        ].map(([label, value]) => (
                                            <div className="col-md-6" key={label}>
                                                <strong className="d-block text-muted small">{label}</strong>
                                                <span>{value || 'Not provided'}</span>
                                            </div>
                                        ))}
                                        <div className="col-12">
                                            <strong className="d-block text-muted small mb-2">Uploaded Documents</strong>
                                            <DocumentLinks
                                                endpoint={API_VIEW_CLIENT_DOCUMENT}
                                                ownerType="client"
                                                ownerId={selectedClient.id}
                                                documents={[
                                                    { field: 'client_uid_upload', label: 'View UID', available: selectedClient.has_client_uid_upload },
                                                    { field: 'client_pan_upload_path', label: 'View PAN', available: selectedClient.has_client_pan_upload },
                                                    { field: 'client_ration_card_upload_path', label: 'View Ration Card', available: selectedClient.has_client_ration_card_upload },
                                                ]}
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setIsClientDetailOpen(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ClientManagement;
